/**
 * DealsPanel Component
 * Shows current Netto promotions with catalogue info and top essentials
 */

import React, { useMemo, useState } from 'react';
import { Tag, TrendingDown, Calendar, Plus, ChevronRight, Percent, MapPin } from 'lucide-react';
import { findMatchingDeals, getCurrentDeals, type NettoDeal } from '../services/nettoDealsService';
import { AllDealsModal } from './AllDealsModal';
import type { List } from '../api/types';

interface DealsPanelProps {
  lists: List[];
  catalogueName?: string;
  validFrom?: string;
  validUntil?: string;
  onAddDealToList: (deal: NettoDeal) => void;
}

// Top 5 essential products (alimentaire de base)
const ESSENTIAL_CATEGORIES = ['Frais', 'Épicerie', 'Fruits & Légumes', 'Boucherie'];

function getEssentialDeals(allDeals: NettoDeal[]): NettoDeal[] {
  return allDeals
    .filter(deal => ESSENTIAL_CATEGORIES.includes(deal.category))
    .slice(0, 5);
}

export function DealsPanel({ 
  lists, 
  catalogueName = 'Catalogue Netto',
  validFrom,
  validUntil,
  onAddDealToList 
}: DealsPanelProps) {
  const [showAllDeals, setShowAllDeals] = useState(false);
  
  const { matchingDeals, allDeals, essentialDeals } = useMemo(() => {
    // Get all item names from all active lists
    const allItemNames = lists
      .filter(list => !list.archivedAt)
      .flatMap(list => list.items || [])
      .filter(item => !item.checked)
      .map(item => item.name);
    
    const all = getCurrentDeals();
    
    return {
      matchingDeals: findMatchingDeals(allItemNames),
      allDeals: all,
      essentialDeals: getEssentialDeals(all)
    };
  }, [lists]);

  const displayDeals = matchingDeals.length > 0 ? matchingDeals.slice(0, 3) : essentialDeals.slice(0, 3);
  
  // Format dates
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'long'
    });
  };

  return (
    <>
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl shadow-lg border-2 border-red-200 overflow-hidden">
        {/* Header with Catalogue Info */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <Tag className="text-red-600" size={20} />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">
                  {matchingDeals.length > 0 ? '🎉 Bons plans pour votre liste !' : catalogueName}
                </h3>
                <div className="flex items-center gap-2 text-red-100 text-sm">
                  <Calendar size={14} />
                  <span>
                    {validFrom && validUntil 
                      ? `Valable du ${formatDate(validFrom)} au ${formatDate(validUntil)}`
                      : 'Promotions en cours'
                    }
                  </span>
                </div>
              </div>
            </div>
            
            {/* View All Button */}
            <button
              onClick={() => setShowAllDeals(true)}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              <span className="hidden sm:inline">Voir tout</span>
              <span className="sm:hidden">Tout</span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Top 5 Essentials Section */}
        {matchingDeals.length === 0 && (
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-center gap-2 mb-3">
              <Percent className="text-red-600" size={18} />
              <h4 className="font-bold text-slate-900">Top 5 Produits de Base en Promo</h4>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {essentialDeals.map((deal, index) => (
                <button
                  key={index}
                  onClick={() => onAddDealToList(deal)}
                  className="bg-white rounded-lg p-2 border border-red-100 hover:border-red-400 hover:shadow-md transition-all group"
                  title={`${deal.brand ? deal.brand + ' - ' : ''}${deal.product}${deal.size ? ' ' + deal.size : ''} - ${deal.discount}`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">
                      {deal.category === 'Frais' ? '🧀' : 
                       deal.category === 'Épicerie' ? '🍞' :
                       deal.category === 'Fruits & Légumes' ? '🍎' :
                       deal.category === 'Boucherie' ? '🥩' : '🛒'}
                    </div>
                    <p className="text-xs font-semibold text-slate-900 truncate group-hover:text-red-600">
                      {deal.brand || deal.product.split(' ')[0]}
                    </p>
                    <span className="text-xs font-bold text-red-600">{deal.discount}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Featured Deals */}
        <div className="p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-700">
            {matchingDeals.length > 0 
              ? `${matchingDeals.length} promo${matchingDeals.length > 1 ? 's' : ''} trouvée${matchingDeals.length > 1 ? 's' : ''} • Cliquez pour ajouter`
              : 'Promotions de la semaine • Cliquez pour ajouter'
            }
          </p>
          
          {displayDeals.map((deal, index) => (
            <button
              key={index}
              onClick={() => onAddDealToList(deal)}
              className="w-full bg-white rounded-xl p-4 border-2 border-red-100 hover:border-red-400 hover:shadow-lg transition-all text-left group"
              title={deal.description}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  {/* Product name + brand */}
                  <div className="flex items-center gap-2 mb-2">
                    {deal.brand && (
                      <span className="bg-slate-100 text-slate-700 text-xs font-bold px-2 py-0.5 rounded">
                        {deal.brand}
                      </span>
                    )}
                    <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                      {deal.category}
                    </span>
                  </div>
                  
                  <h4 className="font-bold text-slate-900 group-hover:text-red-600 transition-colors mb-1">
                    {deal.product}
                  </h4>
                  
                  {/* Size */}
                  {deal.size && (
                    <p className="text-xs text-gray-600 mb-2">
                      Format: {deal.size}
                    </p>
                  )}
                  
                  {/* Price + Discount */}
                  <div className="flex items-center gap-3 text-sm mb-2">
                    <div className="flex items-center gap-1 text-red-600 font-semibold">
                      <TrendingDown size={14} />
                      <span>{deal.discount}</span>
                    </div>
                    
                    {deal.originalPrice && (
                      <span className="text-gray-400 text-xs line-through">
                        {deal.originalPrice}
                      </span>
                    )}
                    
                    {deal.price && (
                      <span className="text-green-700 font-bold text-lg">
                        {deal.price}
                      </span>
                    )}
                  </div>
                  
                  {/* Location */}
                  {deal.location && (
                    <div className="flex items-start gap-1 text-gray-500 text-xs">
                      <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                      <span>{deal.location}</span>
                    </div>
                  )}
                </div>
                
                {/* Add button */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 group-hover:bg-red-600 rounded-lg flex items-center justify-center transition-colors">
                    <Plus className="text-red-600 group-hover:text-white" size={20} />
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="bg-red-50 p-3 border-t border-red-100 flex items-center justify-between">
          <p className="text-xs text-red-700 font-medium">
            📍 Valable dans votre magasin Netto • Sous réserve de disponibilité
          </p>
          <span className="text-xs font-bold text-red-600">
            {allDeals.length} promos totales
          </span>
        </div>
      </div>

      {/* All Deals Modal */}
      <AllDealsModal
        isOpen={showAllDeals}
        onClose={() => setShowAllDeals(false)}
        deals={allDeals}
        onAddDeal={onAddDealToList}
        catalogueName={catalogueName}
        validFrom={validFrom}
        validUntil={validUntil}
      />
    </>
  );
}
